import { z } from 'zod';
import { GiftCertificateCodeSchema } from './gift-certificate';
import { billQuantity, billTotalMinor } from './visit';
// Owner 24.10.2026: `qr_transfer` — the client scans the studio QR code (Wise · bunq · Revolut).
export const PAYMENT_METHODS = ['card_terminal', 'mbway', 'qr_transfer', 'cash', 'bank_transfer', 'gift_card'];
export const PaymentMethodSchema = z.enum(PAYMENT_METHODS);
/** Methods that can pay what a gift certificate does not cover. */
export const REMAINDER_METHODS = [
    'card_terminal',
    'mbway',
    'qr_transfer',
    'cash',
    'bank_transfer',
];
/**
 * How the booking relates to the CRM:
 * - `crm_booked`  — created in the CRM by Hub (CRM writes enabled)
 * - `requested`   — CRM writes are disabled: staff must confirm/enter it in the CRM
 * - `from_crm`    — booking already existed in the CRM (customer checked in at the kiosk)
 */
export const BookingStatusSchema = z.enum(['crm_booked', 'requested', 'from_crm']);
const PhoneSchema = z
    .string()
    .transform((s) => s.replace(/[\s()-]/g, ''))
    .pipe(z.string().regex(/^\+?\d{9,15}$/, 'phone'));
/** Portuguese NIF: 9 digits with a mod-11 check digit. Optional on the kiosk. */
export function isValidNif(nif) {
    if (!/^\d{9}$/.test(nif))
        return false;
    const digits = nif.split('').map(Number);
    const sum = digits.slice(0, 8).reduce((acc, d, i) => acc + d * (9 - i), 0);
    const check = 11 - (sum % 11);
    return (check >= 10 ? 0 : check) === digits[8];
}
/** Normalises Portuguese numbers to E.164 (+351…); keeps other international numbers. */
export function normalizePhone(raw) {
    const s = raw.replace(/[\s()-]/g, '');
    if (s.startsWith('+'))
        return s;
    if (s.startsWith('00'))
        return `+${s.slice(2)}`;
    if (/^9\d{8}$/.test(s) || /^2\d{8}$/.test(s))
        return `+351${s}`;
    return `+${s}`;
}
export const CustomerInputSchema = z.object({
    fullName: z.string().trim().min(2).max(80),
    phone: PhoneSchema,
    // The Parede online booking requires an email (Altegio setting, verified 14.10.2026).
    email: z.string().trim().toLowerCase().email().max(120),
    nif: z
        .string()
        .trim()
        .optional()
        .refine((v) => !v || isValidNif(v), 'nif'),
});
export const CreateVisitRequestSchema = z.object({
    idempotencyKey: z.string().uuid(),
    serviceIds: z
        .array(z.string().regex(/^[a-z0-9-]{1,40}$/))
        .min(1)
        .max(3),
    addonIds: z
        .array(z.string().regex(/^[a-z0-9-]{1,40}$/))
        .max(5)
        .default([]),
    /** Retail products for home care (ids `p-…`). */
    productIds: z
        .array(z.string().regex(/^p-[a-z0-9-]{1,37}$/))
        .max(5)
        .default([]),
    staffId: z.string().regex(/^[a-z0-9-]{1,40}$/),
    start: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?([+-]\d{2}:\d{2}|Z)$/),
    customer: CustomerInputSchema,
    consents: z.object({ privacy: z.literal(true), marketing: z.boolean().default(false) }),
    /** The client's comment to the therapist (owner 26.10.2026); goes to the booking comment in the CRM. */
    comment: z.string().trim().max(300).optional(),
    /** Gift for the booking amount chosen by the customer (checked again on the server). */
    giftId: z
        .string()
        .regex(/^[a-z0-9-]{1,40}$/)
        .optional(),
});
/**
 * Kiosk, owner 17.10.2026: a returning client confirms "yes, it's me" on the details step instead
 * of typing the name and e-mail. The kiosk only holds a short-lived token from
 * `POST …/customers/recognize`; the server fills in the name and e-mail it already has.
 * `email` is sent only when no e-mail is on file.
 */
export const RecognizedCustomerSchema = z.object({
    recognized: z.string().regex(/^[A-Za-z0-9_-]{20,64}$/),
    phone: PhoneSchema,
    email: z.string().trim().toLowerCase().email().max(120).optional(),
});
export const KioskCreateVisitRequestSchema = CreateVisitRequestSchema.extend({
    customer: z.union([CustomerInputSchema, RecognizedCustomerSchema]),
});
/** "Ana Maria Silva" → "Ana S."; one word stays as it is. */
export function shortName(fullName) {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return '';
    if (parts.length === 1)
        return parts[0];
    return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
}
/**
 * Owner 17.10.2026: first two letters + "•••" + the whole domain ("an•••@gmail.com"), so the client
 * recognises the address; one letter only when the name part is 1–2 characters long.
 */
export function maskEmail(email) {
    const at = email.lastIndexOf('@');
    if (at < 1 || at === email.length - 1)
        return undefined;
    const local = email.slice(0, at);
    const keep = local.length <= 2 ? 1 : 2;
    return `${local.slice(0, keep)}•••@${email.slice(at + 1)}`;
}
export function firstName(fullName) {
    return fullName.trim().split(/\s+/)[0] ?? '';
}
export function maskPhone(phone) {
    return phone.length <= 4 ? '••••' : `${'•'.repeat(Math.max(0, phone.length - 3))}${phone.slice(-3)}`;
}
export function toKioskView(v) {
    return {
        id: v.id,
        start: v.start,
        durationMin: v.durationMin,
        staffId: v.staffId,
        bookingStatus: v.bookingStatus,
        state: v.state,
        payment: { state: v.payment.state, method: v.payment.method },
        firstName: firstName(v.customer.fullName),
        // The bill: what was ordered or, once the therapist recorded it, what was performed.
        lines: v.lines
            .map((l) => ({
            serviceId: l.serviceId,
            title: l.title,
            kind: l.kind,
            unitPriceMinor: l.unitPriceMinor,
            quantity: billQuantity(l, v),
        }))
            .filter((l) => l.quantity > 0),
        totalMinor: billTotalMinor(v),
        bookingGift: v.bookingGift
            ? { id: v.bookingGift.id, title: v.bookingGift.title, takeHome: v.bookingGift.takeHome }
            : undefined,
        giftDelivery: v.giftDelivery?.method,
    };
}
export const AddProductsRequestSchema = z.object({
    productIds: z
        .array(z.string().regex(/^p-[a-z0-9-]{1,37}$/))
        .min(1)
        .max(5),
});
/** Certificate number recorded at sale: same rules as everywhere (spaces dropped, upper case). */
const CertificateCodeSchema = GiftCertificateCodeSchema;
/** Tablet: book the next appointment for the same customer right after the treatment. */
export const RebookRequestSchema = z.object({
    start: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?([+-]\d{2}:\d{2}|Z)$/),
    /** Defaults to the treatments of the current visit. */
    serviceIds: z
        .array(z.string().regex(/^[a-z0-9-]{1,40}$/))
        .min(1)
        .max(3)
        .optional(),
    /** Another therapist of the studio (owner 17.10.2026); defaults to the therapist of the current visit. */
    staffId: z
        .string()
        .regex(/^[a-z0-9-]{1,40}$/)
        .optional(),
});
/**
 * Tablet, owner 15.10.2026: after "Client arrived" the therapist may change the main treatment
 * (e.g. Express → Optimal) and add extras; after "Finish" (before payment) only add extras.
 */
export const ChangeServicesRequestSchema = z
    .object({
    replace: z
        .object({
        fromServiceId: z.string().regex(/^[a-z0-9-]{1,40}$/),
        toServiceId: z.string().regex(/^[a-z0-9-]{1,40}$/),
    })
        .refine((r) => r.fromServiceId !== r.toServiceId, 'same treatment')
        .optional(),
    addIds: z
        .array(z.string().regex(/^[a-z0-9-]{1,40}$/))
        .max(5)
        .default([]),
})
    .refine((r) => r.replace || r.addIds.length > 0, 'nothing to change');
export const PerformedRequestSchema = z.object({
    performed: z
        .array(z.object({
        serviceId: z.string().regex(/^[a-z0-9-]{1,40}$/),
        quantity: z.number().int().min(0).max(5),
        /** Certificate number, required for a gift line that is handed over. */
        code: CertificateCodeSchema.optional(),
    }))
        .max(10),
    /**
     * Owner 02.11.2026: a visit with a free gift for the booking amount (face / hand mask to take
     * home, LED) is finished only once the therapist confirms the gift was given.
     */
    giftHandedOver: z.boolean().optional(),
});
export const GiftCardTextSchema = z.object({
    to: z.string().trim().min(1).max(60),
    from: z.string().trim().min(1).max(60),
    message: z.string().trim().max(300).optional(),
});
export const CreateGiftSaleRequestSchema = z
    .object({
    idempotencyKey: z.string().uuid(),
    giftId: z.string().regex(/^gift-\d{3,6}$/),
    /** Buyer's mobile (E.164 from the kiosk country picker). */
    phone: PhoneSchema,
    /**
     * `pickup`: the envelope is handed over in the studio after paying at the tablet (10–15 min).
     * `email`: a request — staff contact the buyer by e-mail to agree the payment, then send the
     * certificate (owner, 15.10.2026). No money moves online in this phase.
     */
    delivery: z.enum(['pickup', 'email']).default('pickup'),
    email: z.string().email().max(120).optional(),
    /** E-mail certificate: who it is for, who it is from and a wish (owner 15.10.2026). */
    card: GiftCardTextSchema.optional(),
})
    .refine((r) => r.delivery !== 'email' || Boolean(r.email), { message: 'email required', path: ['email'] })
    .refine((r) => r.delivery !== 'email' || Boolean(r.card), { message: 'card required', path: ['card'] });
/** Version of the confirmation text the client agreed to (kept with the record as evidence). */
export const QUESTIONNAIRE_TEXT_VERSION = '2026-10-14';
export const QuestionnaireRequestSchema = z.object({
    answer: z.enum(['confirmed', 'needed']),
    textVersion: z.literal(QUESTIONNAIRE_TEXT_VERSION),
});
// ───────────── discount and tip at payment (owner 15.10.2026) ─────────────
/** Why the therapist gave a discount — always recorded, reported and written to Altegio. */
export const DISCOUNT_REASONS = [
    'therapist_late',
    'shorter_service',
    'therapist_fault',
    'management',
    'voucher',
    'other',
];
/** Quick amounts on the tablet (any amount can be typed). */
export const DISCOUNT_PRESETS_MINOR = [500, 1000, 1500, 2000];
/** Sanity limits, cents. */
export const MAX_DISCOUNT_MINOR = 50_000;
export const MAX_TIP_MINOR = 20_000;
/** Tips can be left by card, MB WAY or cash (not from a gift certificate). */
export const TIP_METHODS = ['card_terminal', 'mbway', 'cash'];
export const PaymentDiscountSchema = z
    .object({
    amountMinor: z.number().int().min(100).max(MAX_DISCOUNT_MINOR),
    reason: z.enum(DISCOUNT_REASONS),
    /** Required for "other"; optional otherwise (e.g. the voucher number). */
    note: z.string().trim().max(200).optional(),
})
    .refine((d) => d.reason !== 'other' || (d.note ?? '').length >= 3, {
    message: 'note required for other',
    path: ['note'],
});
export const PaymentTipSchema = z.object({
    amountMinor: z.number().int().min(100).max(MAX_TIP_MINOR),
    method: z.enum(TIP_METHODS),
});
/**
 * Splits a discount over priced units (cents, largest remainder) so the parts add up exactly.
 * Never more than a unit's price; the part above the total is returned as `leftMinor`
 * (e.g. a discount bigger than the services written to the CRM).
 */
export function spreadDiscount(pricesMinor, discountMinor) {
    const total = pricesMinor.reduce((a, b) => a + b, 0);
    const d = Math.min(discountMinor, total);
    if (total <= 0 || d <= 0)
        return { parts: pricesMinor.map(() => 0), leftMinor: discountMinor };
    const exact = pricesMinor.map((p) => (p * d) / total);
    const parts = exact.map(Math.floor);
    let rest = d - parts.reduce((a, b) => a + b, 0);
    const order = exact.map((e, i) => ({ i, frac: e - Math.floor(e) })).sort((a, b) => b.frac - a.frac);
    for (const { i } of order) {
        if (rest <= 0)
            break;
        if (parts[i] < pricesMinor[i]) {
            parts[i] = parts[i] + 1;
            rest--;
        }
    }
    return { parts, leftMinor: discountMinor - d };
}
/** Upper limit for a typed final price, cents. */
export const MAX_FINAL_PRICE_MINOR = 100_000;
/**
 * "Upgrade" (owner 15.10.2026): the therapist types the final amount instead of the bill
 * (e.g. 55 € instead of 60 €, or more after a longer treatment). Only the amount is recorded.
 */
export const PaymentUpgradeSchema = z.object({
    finalMinor: z.number().int().min(0).max(MAX_FINAL_PRICE_MINOR),
});
/** Splits an increase over priced units in proportion to their prices (cents, exact sum). */
export function spreadIncrease(pricesMinor, increaseMinor) {
    const total = pricesMinor.reduce((a, b) => a + b, 0);
    if (!pricesMinor.length || increaseMinor <= 0)
        return pricesMinor.map(() => 0);
    const weights = total > 0 ? pricesMinor : pricesMinor.map(() => 1);
    const wsum = weights.reduce((a, b) => a + b, 0);
    const exact = weights.map((w) => (w * increaseMinor) / wsum);
    const parts = exact.map(Math.floor);
    let rest = increaseMinor - parts.reduce((a, b) => a + b, 0);
    const order = exact.map((e, i) => ({ i, frac: e - Math.floor(e) })).sort((a, b) => b.frac - a.frac);
    for (const { i } of order) {
        if (rest <= 0)
            break;
        parts[i] = parts[i] + 1;
        rest--;
    }
    return parts;
}
/** Products from the accounting system in the visit bill: absolute quantity per product (0 removes). */
export const SetShopProductsRequestSchema = z.object({
    items: z
        .array(z.object({ productId: z.number().int().positive(), qty: z.number().int().min(0).max(20) }))
        .min(1)
        .max(20),
});
/**
 * Language of the tablet: the `[Hub]` lines Hub adds to the CRM booking comment are written in it
 * (owner 21.10.2026: the language the therapist works in). Default Portuguese.
 */
export const NoteLangSchema = z.enum(['pt', 'en', 'ru']);
export const ConfirmPaymentRequestSchema = z.object({
    lang: NoteLangSchema.optional(),
    /** Discount given by the therapist (before the certificate and the money). */
    discount: PaymentDiscountSchema.optional(),
    /** Final amount typed by the therapist instead of the bill (not together with a discount). */
    upgrade: PaymentUpgradeSchema.optional(),
    /** Tip received on top of the bill (recorded for the report; not revenue of the studio). */
    tip: PaymentTipSchema.optional(),
    /** With `certificateCode`: the method for the remainder, or `gift_card` if the certificate covers all. */
    method: PaymentMethodSchema,
    pin: z.string().regex(/^\d{4,8}$/),
    /** Gift certificate used for this payment (covers min(balance, bill)). */
    certificateCode: GiftCertificateCodeSchema.optional(),
    /** NIF for the invoice, given by the client at payment (not asked on the kiosk). */
    nif: z
        .string()
        .trim()
        .optional()
        .refine((v) => !v || isValidNif(v), 'nif'),
});
export function toStaffVisit(v) {
    const { customer, giftDelivery, ...rest } = v;
    const out = {
        ...rest,
        customer: {
            fullName: customer.fullName,
            ...(customer.nif ? { nif: customer.nif } : {}),
            hasEmail: Boolean(customer.email),
        },
    };
    if (giftDelivery) {
        const { email: _email, ...g } = giftDelivery;
        void _email;
        out.giftDelivery = g;
    }
    return out;
}
/** Discounts and tips of the paid visits of a day (visits in any order). */
export function buildDayReport(date, currency, visits) {
    const paid = visits
        .filter((v) => v.payment.state === 'succeeded' && v.payment.confirmedAt)
        .sort((a, b) => a.payment.confirmedAt.localeCompare(b.payment.confirmedAt));
    const services = (v) => v.lines.filter((l) => l.performed > 0).map((l) => l.title);
    const discounts = paid
        .filter((v) => v.payment.discount)
        .map((v) => {
        const d = v.payment.discount;
        return {
            visitId: v.id,
            at: v.payment.confirmedAt,
            staffId: v.staffId,
            clientName: v.customer.fullName,
            services: services(v),
            beforeMinor: d.beforeMinor,
            amountMinor: d.amountMinor,
            afterMinor: d.beforeMinor - d.amountMinor,
            reason: d.reason,
            ...(d.note ? { note: d.note } : {}),
            crm: d.crm ?? 'manual',
        };
    });
    const upgrades = paid
        .filter((v) => v.payment.upgrade)
        .map((v) => ({
        visitId: v.id,
        at: v.payment.confirmedAt,
        staffId: v.staffId,
        clientName: v.customer.fullName,
        services: services(v),
        beforeMinor: v.payment.upgrade.beforeMinor,
        finalMinor: v.payment.upgrade.finalMinor,
        crm: v.payment.upgrade.crm ?? 'manual',
    }));
    const tips = paid
        .filter((v) => v.payment.tip)
        .map((v) => ({
        visitId: v.id,
        at: v.payment.confirmedAt,
        staffId: v.staffId,
        clientName: v.customer.fullName,
        amountMinor: v.payment.tip.amountMinor,
        method: v.payment.tip.method,
    }));
    const byReason = DISCOUNT_REASONS.map((reason) => {
        const of = discounts.filter((d) => d.reason === reason);
        return { reason, count: of.length, amountMinor: of.reduce((s, d) => s + d.amountMinor, 0) };
    }).filter((r) => r.count > 0);
    const staffIds = [...new Set(tips.map((t) => t.staffId))];
    return {
        date,
        currency,
        discounts,
        upgrades,
        tips,
        totals: {
            discountMinor: discounts.reduce((s, d) => s + d.amountMinor, 0),
            byReason,
            tipsByStaff: staffIds.map((staffId) => {
                const of = tips.filter((t) => t.staffId === staffId);
                return { staffId, amountMinor: of.reduce((s, t) => s + t.amountMinor, 0), count: of.length };
            }),
        },
    };
}
//# sourceMappingURL=visit-model.js.map